<script setup lang="ts">
import { computed, type CSSProperties, type PropType } from 'vue';
import { h1Props, NH1, NH2, NH3, NH4, NH5, NH6 } from 'naive-ui';
import {
  gaiaFontWeight,
  gaiaFonts,
  gaiaHeadingSize,
  gaiaTextTone,
  gaiaTracking,
} from '../themes/tokens';

const props = defineProps({
  ...h1Props,
  level: {
    type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
    default: 1,
  },
  /** display = the light, wide-tracked greeting heading (mock 5a "Good morning, …"). */
  variant: {
    type: String as PropType<'default' | 'display'>,
    default: 'default',
  },
  /**
   * Display size override. Undefined keeps naive's per-level heading size;
   * `hero` is the login brand headline, `page` the auth-card title.
   */
  size: {
    type: String as PropType<keyof typeof gaiaHeadingSize>,
    default: undefined,
  },
  /**
   * Weight override. Undefined keeps whatever the variant asks for — `display`
   * is 400 by design. Applied after the variant below, so an explicit weight
   * wins, the same way `GaiaText`'s does over its `label` recipe.
   */
  weight: {
    type: String as PropType<keyof typeof gaiaFontWeight>,
    default: undefined,
  },
  /**
   * Set the heading in the body face instead of the serif. Exactly one heading
   * does this — the login brand panel's, which sits beside serif auth cards and
   * is meant to read as a different voice from them.
   */
  sans: {
    type: Boolean,
    default: false,
  },
  /**
   * Heading colour. Defaults to the ink token rather than inheriting: naive's
   * Typography sets its own heading colour (#1F2225), which is close enough to
   * ours to look intentional and is not in the palette at all.
   */
  tone: {
    type: String as PropType<keyof typeof gaiaTextTone>,
    default: 'ink',
  },
});

/**
 * Everything naive's Typography theme also sets on `n-h*` — weight and colour —
 * has to arrive inline: a scoped class ties with naive's own rule on
 * specificity, so which one wins comes down to stylesheet injection order.
 * font-family and line-height stay in CSS below, where nothing competes.
 */
const headingStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {};
  if (props.size) style.fontSize = gaiaHeadingSize[props.size];
  if (props.variant === 'display') {
    style.fontWeight = gaiaFontWeight.regular;
    style.letterSpacing = gaiaTracking.display;
  }
  // After the variant, so an explicit weight wins over `display`'s 400.
  if (props.weight) style.fontWeight = gaiaFontWeight[props.weight];
  style.color = gaiaTextTone[props.tone];
  return style;
});

const headerComponent = computed(() => [NH1, NH2, NH3, NH4, NH5, NH6][props.level - 1]);

const headingFont = gaiaFonts.heading;
const navFont = gaiaFonts.nav;
</script>

<template>
  <component
    :is="headerComponent"
    :type="props.type"
    :prefix="props.prefix"
    :align-text="props.alignText"
    class="gaia-header"
    :class="{
      'gaia-header--display': props.variant === 'display',
      'gaia-header--sans': props.sans,
    }"
    :style="headingStyle"
  >
    <slot />
  </component>
</template>

<style scoped>
/* naive's Typography theme has no font-family var (only sizes, weights and
   colors), so the heading serif is applied here — this wrapper is the single
   place headings are drawn, which is what keeps the rest of the app on Geist.
   `optical-sizing: auto` is the default, but stated because it is the point of
   shipping the variable face: the opsz axis retunes the letterforms between a
   34px hero and a 14px h6. */
.gaia-header {
  font-family: v-bind(headingFont);
  font-optical-sizing: auto;
  /* naive's Typography theme gives every heading a `28px 0 20px` block margin.
     Gaia pages space themselves with n-flex/n-grid gaps, so that margin only
     ever double-counts — and in a title row (`n-flex align="center"`) its
     asymmetry pushes the heading's text below the centre line the row aligns
     its siblings to, so a status chip beside a page title sits visibly off.
     Every mock draws its h1 at `margin: 0`; a caller that wants space asks its
     layout for it.

     `Typography.headerMargin1..6` would carry this, but the theme reaches the
     ~60 raw `n-h1`/`n-h2`/`n-h3` still left in older views too, and those do
     rely on the margin for their rhythm. So it stays scoped to the wrapper
     until that migration is done. */
  margin: 0;
}

/* naive has no per-heading weight, tracking or line-height var either, so the
   display treatment lives here too. The tight leading is not only taste: at
   this size the inherited body line-height stacks several px of half-leading
   above the letters, which pushes the home greeting visibly below the right
   rail's section header even though both columns start at the same
   `gaiaShellPadding.top`. */
.gaia-header--sans {
  font-family: v-bind(navFont);
}

.gaia-header--display {
  line-height: 1.15;
}
</style>
